import React, { useState } from 'react';
import {
    SimpleForm,
    SimpleShowLayout,
    TextField,
    FunctionField,
    NumberField,
    TextInput,
    required,
    usePermissions,
    maxLength,
    Button,
    useDataProvider,
    useGetOne,
    useTranslate,
} from 'react-admin';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import { noCharactersFromList } from '@/components/global/validators/customValidators.jsx';
import { useDrawer } from '@/composables/useDrawer.js';
import { useQuickEditField } from '@/composables/useQuickEditField.js';
import EditableField from '@/components/global/fields/EditableField';
import { useEditingStates } from '@/components/service/hooks/useEditingStates';
import { ContractorLogoField } from '@/components/contractors/ContractorLogoField.jsx';
import GroupDevicesStateField from '@/components/deviceGroup/GroupDevicesStateField.jsx';
import GroupSoftwareField from '@/components/deviceGroup/GroupSoftwareField.jsx';
import DeviceGroupDetailsCollapse from '@/components/deviceGroup/details/DeviceGroupDetailsCollapse.jsx';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import EditIcon from '@/assets/icons/edit-icon.svg?react';
import IsActiveField from '@/components/global/fields/IsActiveField.jsx';
import { ChangeHistory } from '@/components/global/history/ChangeHistory.jsx';

export const DeviceGroupShow = ({ handleClose, elementId }) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const { permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const editingStates = useEditingStates();
    const { isEditing, FIELDS, startEditingContractorNameGroup, startEditingConfigGroup } = editingStates;
    const [isDevicesOpen, setIsDevicesOpen] = useState(false);
    const isAdmin = permissions.isAdmin;
    const isAdminOrBok = permissions?.isAdmin || permissions?.isBok;
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const { save, isLoadingEdit } = useQuickEditField('device-group', elementId, editingStates);

    const { data: device, isLoading, error } = useGetOne('device-group', { id: elementId });

    if (error) {
        console.error('Error fetching device group:', error);
        return <div>{translate('resources.deviceGroup.labels.loadingError')}</div>;
    }

    if (isLoading || !permissions || isLoadingEdit) return null;

    return (
        <Box>
            <Box className='popup-wrapper popup-wrapper-top'>
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>{translate('common.details')}</Typography>
                    <Box className='popup-box__more-actions-grey' sx={{ height: '44px' }}>
                        <CustomButton
                            color='secondary'
                            label={translate('resources.deviceGroup.labels.editDeviceGroup')}
                            variant='contained'
                            size={isDesktop ? 'large' : 'small'}
                            startIcon={<EditIcon />}
                            onClick={() => openDrawer(elementId, 'device-group-edit')}
                        />
                        <CustomCloseButton onClose={handleClose} />
                    </Box>
                </Box>
            </Box>
            <Box className='popup-wrapper border-bottom'>
                <Box className='popup-box'>
                    <SimpleForm
                        className='simple-form-details-group'
                        toolbar={false}
                        onSubmit={save}
                        record={device}
                        sx={{
                            margin: '16px 0 !important',
                        }}
                    >
                        <SimpleShowLayout record={device} className={isDesktop ? 'two-columns-show-layout' : ''}>
                            <SimpleShowLayout className='no-label image' sx={{ marginBottom: isDesktop ? '0' : '32px', maxWidth: 'max-content' }}>
                                <ContractorLogoField contractorSource='contractor' label={translate('resources.contractor.fields.currentLogo')} />
                            </SimpleShowLayout>
                            <Box
                                className='details-sample-list'
                                sx={{
                                    '& .MuiStack-root.ra-field': {
                                        padding: '10px 0 !important',
                                        '& .RaLabeled-label': {
                                            width: '120px !important',
                                            minWidth: '120px !important',
                                        },
                                    },
                                    '& .MuiTypography-root.MuiTypography-body2': {
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                    '& .editable-group-name': {
                                        '& .MuiTypography-root.RaLabeled-label': {
                                            alignSelf: 'center',
                                        },
                                    },
                                    '& .MuiCardContent-root': {},
                                }}
                            >
                                <SimpleShowLayout>
                                    <FunctionField
                                        source='contractorId'
                                        label={translate('common.contractor')}
                                        sortBy='contractor.name'
                                        className='bold'
                                        render={record => (
                                            <CustomButton
                                                className='list-group'
                                                onClick={isAdminOrBok ? () => openDrawer(record.contractorId, 'contractor-edit') : undefined}
                                                label={record.contractor.name}
                                                sx={{ cursor: isAdminOrBok ? 'pointer' : 'default' }}
                                            />
                                        )}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout className='editable-group-name'>
                                    <EditableField
                                        label={translate('resources.deviceGroup.fields.name')}
                                        isEditing={isEditing(FIELDS.CONTRACTOR_NAME_GROUP)}
                                        onEdit={isAdmin ? startEditingContractorNameGroup : () => {}}
                                        displayValue={device?.name}
                                        isFilled={record => record.name}
                                        editComponent={
                                            <TextInput
                                                source='name'
                                                label={translate('resources.deviceGroup.fields.name')}
                                                validate={[
                                                    required(translate('common.validation.required')),
                                                    maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                                                    noCharactersFromList('$%^"",.;'),
                                                ]}
                                                fullWidth
                                            />
                                        }
                                        canEdit={isAdmin}
                                        className={isEditing(FIELDS.CONTRACTOR_NAME_GROUP) ? 'editable-field--active bold' : 'bold'}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <NumberField className='bold' source='deviceCount' label={translate('resources.deviceGroup.fields.deviceCount')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <NumberField className='bold' source='newDeviceCount' label={translate('resources.deviceGroup.fields.newDeviceCount')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout className='label-center'>
                                    <GroupDevicesStateField label={translate('resources.deviceGroup.labels.devicesState')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <GroupSoftwareField label={translate('resources.deviceGroup.labels.software')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <EditableField
                                        label={translate('resources.deviceGroup.labels.config')}
                                        sx={{
                                            width: '100%',
                                            '& .MuiBox-root': {
                                                width: '100%',
                                            },
                                            '& .editable-field-content, textarea': {
                                                wordBreak: 'break-word',
                                                whiteSpace: 'normal',
                                                fontSize: '12px !important',
                                            },
                                            '& .MuiFormHelperText-root': {
                                                display: 'none',
                                            },
                                        }}
                                        isEditing={isEditing(FIELDS.CONFIG_GROUP)}
                                        onEdit={isAdmin ? startEditingConfigGroup : () => {}}
                                        displayValue={record => record.config}
                                        isFilled={record => record.config}
                                        isHtml={true}
                                        editComponent={
                                            <TextInput
                                                source='config'
                                                label={translate('resources.deviceGroup.labels.config')}
                                                validate={[required(translate('common.validation.required'))]}
                                                multiline
                                                fullWidth
                                            />
                                        }
                                        canEdit={isAdmin}
                                        isHorizontal={false}
                                        className={isEditing(FIELDS.CONFIG_GROUP) ? 'editable-field--active' : ''}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <FunctionField
                                        render={record => (
                                            <Box display='flex' flexDirection='row' alignItems='center' gap='16px' flexWrap='wrap'>
                                                <Box flex={1}>
                                                    <TextField
                                                        source='fileChecksum'
                                                        component='span'
                                                        emptyText={translate('resources.deviceGroup.labels.noFile')}
                                                        sx={{ wordBreak: 'break-all' }}
                                                    />
                                                </Box>
                                                {record.fileChecksum && (
                                                    <Box flex={1}>
                                                        <Button
                                                            color='info'
                                                            size='small'
                                                            label={translate('resources.deviceGroup.labels.download')}
                                                            onClick={() => dataProvider.downloadFile(`/device-group/${record.id}/data-file`, record.id)}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                        label={translate('resources.deviceGroup.labels.files')}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <FunctionField source='isActive' label={translate('common.active')} render={record => <IsActiveField record={record} />} />
                                </SimpleShowLayout>
                            </Box>
                        </SimpleShowLayout>
                    </SimpleForm>
                </Box>
            </Box>
            <Box className='popup-box'>
                <DeviceGroupDetailsCollapse record={device} elementId={elementId} isDevicesOpen={isDevicesOpen} setIsDevicesOpen={setIsDevicesOpen} />
            </Box>
            <ChangeHistory resource='device-group' recordId={elementId} fieldsSource='resources.deviceGroup.fields' />
        </Box>
    );
};
